// Web Audio API — Block Burst ses sistemi

import blockSoundFile from './Sound Effect/floraphonic-block-heel-footstep-2-184069 (1).mp3';
import gameOverSoundFile from './Sound Effect/mori_sound-fx-game-over-497165.mp3';

let audioCtx = null;
let blockBuffer = null;
let blockPeaks = null;
let blockLoadPromise = null;
let gameOverBuffer = null;
let gameOverLoadPromise = null;

// ── Mute state ────────────────────────────────────────────────────────────────
let _muted = localStorage.getItem('blockBurstMuted') === 'true';

export function isMuted() { return _muted; }

export function setMuted(value) {
  _muted = value;
  localStorage.setItem('blockBurstMuted', value);
}

export function toggleMuted() {
  setMuted(!_muted);
  return _muted;
}

// ── AudioContext ───────────────────────────────────────────────────────────────
function getAudioContext() {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') audioCtx.resume();
  return audioCtx;
}

// ── Peak Finder ───────────────────────────────────────────────────────────────
function findTwoPeaks(buffer, minSepSec = 0.1) {
  const data = buffer.getChannelData(0);
  const sr = buffer.sampleRate;
  const winSize = Math.floor(0.01 * sr);
  const minSepSamples = Math.floor(minSepSec * sr);

  const energies = [];
  for (let i = 0; i < data.length - winSize; i += winSize) {
    let sum = 0;
    for (let j = i; j < i + winSize; j++) sum += data[j] * data[j];
    energies.push({ e: Math.sqrt(sum / winSize), idx: i });
  }

  const sorted = [...energies].sort((a, b) => b.e - a.e);
  const peaks = [];
  for (const c of sorted) {
    if (!peaks.some(p => Math.abs(p.idx - c.idx) < minSepSamples)) {
      peaks.push(c);
      if (peaks.length === 2) break;
    }
  }
  peaks.sort((a, b) => a.idx - b.idx);
  return peaks.map(p => p.idx / sr);
}

// ── Loaders ───────────────────────────────────────────────────────────────────
async function loadBlockSound() {
  if (blockBuffer && blockPeaks) return { buffer: blockBuffer, peaks: blockPeaks };
  if (blockLoadPromise) return blockLoadPromise;

  blockLoadPromise = fetch(blockSoundFile)
    .then(r => r.arrayBuffer())
    .then(ab => getAudioContext().decodeAudioData(ab))
    .then(decoded => {
      blockBuffer = decoded;
      blockPeaks = findTwoPeaks(decoded);
      return { buffer: decoded, peaks: blockPeaks };
    })
    .catch(() => null);

  return blockLoadPromise;
}

async function loadGameOverSound() {
  if (gameOverBuffer) return gameOverBuffer;
  if (gameOverLoadPromise) return gameOverLoadPromise;

  gameOverLoadPromise = fetch(gameOverSoundFile)
    .then(r => r.arrayBuffer())
    .then(ab => getAudioContext().decodeAudioData(ab))
    .then(decoded => { gameOverBuffer = decoded; return decoded; })
    .catch(() => null);

  return gameOverLoadPromise;
}

// Preload both on startup
loadBlockSound();
loadGameOverSound();

// ── Player ────────────────────────────────────────────────────────────────────
function playSegment(buffer, offset, duration, volume = 1) {
  const ctx = getAudioContext();
  const source = ctx.createBufferSource();
  const gain = ctx.createGain();
  source.buffer = buffer;
  source.connect(gain);
  gain.connect(ctx.destination);
  gain.gain.setValueAtTime(volume, ctx.currentTime);
  gain.gain.setValueAtTime(volume, ctx.currentTime + duration * 0.75);
  gain.gain.linearRampToValueAtTime(0.001, ctx.currentTime + duration);
  source.start(ctx.currentTime, offset, duration);
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Blok tutulduğunda: 1. pik */
export async function playPickupSound() {
  if (_muted) return;
  try {
    const result = await loadBlockSound();
    if (!result) return;
    const { buffer, peaks } = result;
    const start = Math.max(0, peaks[0] - 0.02);
    const maxDuration = (peaks[1] - 0.03) - start;
    const duration = Math.min(0.25, Math.max(0.05, maxDuration));
    playSegment(buffer, start, duration, 0.85);
  } catch (e) {}
}

/** Blok bırakıldığında: 2. pik */
export async function playPlaceSound() {
  if (_muted) return;
  try {
    const result = await loadBlockSound();
    if (!result) return;
    const { buffer, peaks } = result;
    const start = Math.max(0, peaks[1] - 0.02);
    const duration = Math.min(0.3, buffer.duration - start);
    playSegment(buffer, start, duration, 1.0);
  } catch (e) {}
}

/** Satır/sütun temizlendiğinde: combo ding */
export function playClearSound(comboCount = 1) {
  if (_muted) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const baseFreqs = [523, 659, 784, 1047];
    const noteCount = Math.min(comboCount + 1, baseFreqs.length);
    for (let i = 0; i < noteCount; i++) {
      const delay = i * 0.07;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(baseFreqs[i], now + delay);
      osc.frequency.exponentialRampToValueAtTime(baseFreqs[i] * 1.02, now + delay + 0.15);
      gain.gain.setValueAtTime(0, now + delay);
      gain.gain.linearRampToValueAtTime(0.3, now + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.3);
      osc.start(now + delay);
      osc.stop(now + delay + 0.31);
    }
  } catch (e) {}
}

/** Game Over sesi */
export async function playGameOverSound() {
  if (_muted) return;
  try {
    const buffer = await loadGameOverSound();
    if (!buffer) return;
    playSegment(buffer, 0, buffer.duration, 0.9);
  } catch (e) {}
}
